import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { useCreateExperimentWithPlots, useCreatePlotsFromGrid, useCreatePlotsFromSequence, useDeleteExperiment } from "@/services/dataService";
import { CreateExperimentInput, Experiment } from "@/types/experiment";
import React, { useCallback, useRef, useState } from "react";
import NewExperimentForm, { PlotMapData } from "./NewExperimentForm";
import { submitNewExperiment } from "./newExperimentSubmit";
import { useRequireSelectedOrgId } from "@/hooks/useRequireSelectedOrgId";
import { useUnsavedChangesGuard } from "@/hooks/useUnsavedChangesGuard";

interface AddExperimentDialogProps {
  trialId: string;
  trialName?: string;
  trialLocation?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onExperimentAdded?: (experiment: Experiment) => void;
  onCreateStart?: (experimentName: string, startedAtMs: number) => void;
  onCreateError?: (error: unknown) => void;
}

const AddExperimentDialog: React.FC<AddExperimentDialogProps> = ({
  trialId,
  trialName,
  trialLocation,
  open,
  onOpenChange,
  onExperimentAdded,
  onCreateStart,
  onCreateError,
}) => {
  const { toast } = useToast();
  const requireOrgId = useRequireSelectedOrgId();
  const createExperimentWithPlots = useCreateExperimentWithPlots();
  const createPlotsFromGrid = useCreatePlotsFromGrid();
  const createPlotsFromSequence = useCreatePlotsFromSequence();
  const deleteExperiment = useDeleteExperiment();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionProgress, setSubmissionProgress] = useState<{ progress: number; message: string } | null>(null);
  const [isDirty, setIsDirty] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Warn before discarding an in-progress experiment (Escape, click-outside, X, Cancel).
  const guard = useUnsavedChangesGuard({ isDirty, onOpenChange });

  // Scroll to show plot map section
  const scrollToPlotMap = useCallback(() => {
    const container = scrollContainerRef.current;
    if (container) {
      // Scroll down to show the plot map section (roughly after the first 3 fields)
      container.scrollTo({ top: 200, behavior: "smooth" });
    }
  }, []);

  const handleSubmit = async (experimentData: CreateExperimentInput, plotMapData?: PlotMapData) => {
    // Capture the organization once: every write of this submission (experiment,
    // plots, rollback) targets it even if the user switches organization mid-flight.
    const orgId = requireOrgId();
    if (!orgId) return;

    setIsSubmitting(true);
    setSubmissionProgress({ progress: 0, message: "Preparing..." });
    // Note: We don't call onCreateStart here - we call it after plots are created
    // so the transition overlay only shows during the navigation, not during creation

    try {
      const createdExperiment = await submitNewExperiment(
        { createExperimentWithPlots, createPlotsFromGrid, createPlotsFromSequence, deleteExperiment },
        {
          orgId,
          trialId,
          experimentData,
          plotMapData,
          onProgress: (progress, message) => {
            setSubmissionProgress({ progress, message });
          },
        }
      );

      if (createdExperiment) {
        // Now that creation is complete, show the transition overlay for navigation
        onCreateStart?.(experimentData.name, Date.now());
        onExperimentAdded?.(createdExperiment);
        onOpenChange(false);
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to create experiment",
        variant: "destructive",
      });
      onCreateError?.(error);
    } finally {
      setIsSubmitting(false);
      setSubmissionProgress(null);
    }
  };

  const handleCancel = () => {
    if (!isSubmitting) {
      guard.requestClose();
    }
  };

  return (
    <>
    <Dialog open={open} onOpenChange={guard.guardedOnOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col p-0">
        <DialogHeader className="px-6 pt-6">
          <DialogTitle>Add New Experiment</DialogTitle>
          <p className="text-sm text-muted-foreground">
            An experiment defines the location(s) where you will be collecting data. Each experiment contains data collection tasks that can be added over the course of a season.
          </p>
        </DialogHeader>
        <div className="overflow-y-auto flex-1 px-6 pb-6" ref={scrollContainerRef}>
            <NewExperimentForm
              trialId={trialId}
              trialName={trialName}
              trialLocation={trialLocation}
              onSubmit={handleSubmit}
              onCancel={handleCancel}
              isSubmitting={isSubmitting}
              submissionProgress={submissionProgress}
              onPlotMapUploaded={scrollToPlotMap}
              onDirtyChange={setIsDirty}
            />
        </div>
      </DialogContent>
    </Dialog>

    {guard.confirmation}
    </>
  );
};

export default AddExperimentDialog;
